"use client"

import styled from "@emotion/styled"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { vehicleCardClicked, vehicleCardViewed } from "@eurodrive/utils/gtm"
import { Checkbox as CheckboxComponent, Typography } from "@mui/material"
import { LeasingCarDto, SaleCarDto } from "api"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useRef } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import CarImagePlaceholder from "./CarImagePlaceholder"
import { CarType, useComparison } from "./contexts/ComparisonContext"
import Checkbox from "./icons/Checkbox"
import CheckboxActive from "./icons/CheckboxActive"

const StyledSwiper = styled(Swiper)`
  & .swiper-wrapper {
    padding-top: 2px;
  }
  padding-bottom: 4px;
`

const StyledSwiperSlide = styled(SwiperSlide)`
  width: 250px !important;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ece9e9;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`

const ImageContainer = styled.div`
  height: 195px;
  position: relative;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  border-bottom: 1px solid #f3f3f3;
`

const Content = styled.div`
  display: flex;
  z-index: 2;
  width: 100%;
  gap: 8px;
  justify-content: space-between;
`

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const BottomContent = styled.div`
  display: flex;
  padding: 16px;
  gap: 16px;
  flex-direction: column;
  text-align: left;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #f3f3f3;
`

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: left;
  gap: 8px;
`

const ChipsFlex = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const Chip = styled.div`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  gap: 10px;
  border-radius: 4px;
  background: #f3f3f3;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 20px;
  text-align: center;
`

// Helper component to track card visibility
function CardWrapper({
  children,
  carId,
  carType,
  price,
}: {
  children: React.ReactNode
  carId: string
  carType: string
  price: string
}) {
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            vehicleCardViewed(carId, carType, price)
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.5 },
    )

    if (cardRef.current) {
      observer.observe(cardRef.current)
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current)
      }
    }
  }, [carId, carType, price])

  return <div ref={cardRef}>{children}</div>
}

export default function CarsSwiper({
  cars,
}: {
  title?: string
  cars: {
    identifier: string | number
    vehicleNumber?: number
    manufacturerCode?: number
    modelCode?: number
    name: string
    image: string
    icon?: string
    chips: string[]
    price: string
    viewedAt?: number // timestamp
    type: "Sale" | "Leasing"
    carData?: SaleCarDto | LeasingCarDto
  }[]
}) {
  const comparison = useComparison()
  return (
    <StyledSwiper
      spaceBetween={12}
      style={{
        margin: 0,
        display: "flex",
        // width: "fit-content",
        width: "100%",
      }}
      slidesPerView={"auto"}
      pagination={{ clickable: true }}
      centerInsufficientSlides
      role="list"
      aria-label="רכבים שנצפו לאחרונה"
    >
      {cars.map((item, index) => {
        // Generate the correct URL based on car type
        const carUrl =
          item.type === "Sale"
            ? `/cars/${item.vehicleNumber}`
            : `/leasing/${item.manufacturerCode}-${item.modelCode}`

        // Generate consistent car ID with prefix
        const carId =
          item.type === "Sale"
            ? `sale-${item.vehicleNumber}`
            : `leasing-${item.manufacturerCode}-${item.modelCode}`

        const carType: CarType = item.type === "Sale" ? "sale" : "leasing"
        const isSelected = comparison.isCarSelected(carId)

        const handleCheckboxClick = (e: React.MouseEvent) => {
          e.preventDefault()
          e.stopPropagation()

          if (isSelected) {
            comparison.removeCar(carId)
          } else {
            // If we have carData, use it, otherwise we can't add to comparison
            if (item.carData) {
              comparison.addCar(item.carData, carType)
            } else {
              console.warn(
                "Cannot add to comparison: carData is missing for",
                item.name,
              )
            }
          }
        }

        const handleCardClick = () => {
          vehicleCardClicked(carId, item.type, "previously_viewed_swiper")
        }

        return (
          <StyledSwiperSlide
            key={`${item.type}-${item.identifier}`}
            role="listitem"
          >
            <CardWrapper carId={carId} carType={item.type} price={item.price}>
              <ImageContainer>
                <Content>
                  <CheckboxContainer onClick={handleCheckboxClick}>
                    <CheckboxComponent
                      icon={<Checkbox />}
                      checkedIcon={<CheckboxActive />}
                      checked={isSelected}
                      inputProps={{
                        "aria-label": `הוספת ${item.name} להשוואה`,
                      }}
                    />
                    <Typography variant="body3">להשוואה</Typography>
                  </CheckboxContainer>
                  {item.icon && (
                    <Image
                      src={getUmbracoMediaUrl(item.icon)}
                      alt=""
                      aria-hidden="true"
                      width={18}
                      height={18}
                      style={{ objectFit: "contain" }}
                    />
                  )}
                </Content>
                {item.image ? (
                  <Image
                    src={getUmbracoMediaUrl(item.image)}
                    alt={item.name}
                    fill
                    sizes="250px"
                    style={{ objectFit: "cover" }}
                  />
                ) : (
                  <CarImagePlaceholder />
                )}
              </ImageContainer>
              <Link
                href={carUrl}
                style={{ textDecoration: "none", color: "inherit" }}
                onClick={handleCardClick}
              >
                <BottomContent>
                  <DetailsContainer>
                    <Typography
                      variant="body3"
                      component={"h3"}
                      fontWeight={400}
                    >
                      {item.name}
                    </Typography>
                    <ChipsFlex>
                      {item.chips.map((chip, i) => (
                        <Chip key={i}>
                          <Typography variant="body2">{chip}</Typography>
                        </Chip>
                      ))}
                    </ChipsFlex>
                  </DetailsContainer>
                  <Divider />

                  <Typography variant="body1">
                    מחיר ₪{parseInt(item.price).toLocaleString()}
                  </Typography>
                </BottomContent>
              </Link>
            </CardWrapper>
          </StyledSwiperSlide>
        )
      })}
    </StyledSwiper>
  )
}
